import * as i0 from '@angular/core';
import { InjectionToken, inject, EnvironmentInjector, ApplicationRef, createNgModule, createComponent, createEnvironmentInjector, Injectable } from '@angular/core';
import { Subject, from, map, take } from 'rxjs';
import { DOCUMENT } from '@angular/common';
class DialogRef {
  constructor() {
    this.closeSubject$ = new Subject();
    this.close$ = this.closeSubject$.asObservable();
  }
  close(data) {
    this.closeSubject$.next(data);
    this.closeSubject$.complete();
  }
}
class DialogTag {
  constructor(identifier, load, config = {}) {
    this.identifier = identifier;
    this.load = load;
    this.config = config;
    this.dialogRef = new InjectionToken(`${this.identifier}DialogRef`);
    this.dialogData = new InjectionToken(`${this.identifier}DialogData`);
  }
}
function isComponentWithModule(component) {
  return component.module !== undefined;
}
function isDefaultExport(component) {
  return component.default !== undefined;
}
let DialogService = /*#__PURE__*/(() => {
  class DialogService {
    constructor() {
      this.injector = inject(EnvironmentInjector);
      this.document = inject(DOCUMENT);
      this.appRef = inject(ApplicationRef);
      this.scrollBarWidth = 0;
    }
    getOverlayContainer() {
      if (!this.overlayContainer) {
        this.overlayContainer = this.document.createElement('div');
        this.overlayContainer.classList.add('px-dialog-overlay');
        this.document.body.appendChild(this.overlayContainer);
      }
      return this.overlayContainer;
    }
    ngOnDestroy() {
      this.overlayContainer?.remove();
    }
    openDialog(tag, data) {
      return from(tag.load()).pipe(map(comp => {
        const dialogRef = new DialogRef();
        const injector = this.createInjector(dialogRef, tag, data);
        const compRef = this.getComponentRef(comp, injector);
        dialogRef.close$.pipe(take(1)).subscribe(() => this.closeDialog(compRef));
        if (tag.config.overflowHidden !== false) {
          this.scrollBarWidth = this.getScrollBarWidth();
          this.addOverFlowClass();
        }
        this.appRef.attachView(compRef.hostView);
        this.getOverlayContainer().appendChild(this._getComponentRootNode(compRef));
        dialogRef.componentInstance = compRef.instance;
        compRef.changeDetectorRef.detectChanges();
        return dialogRef;
      }));
    }
    getScrollBarWidth() {
      if (this.scrollBarWidth) {
        return this.scrollBarWidth;
      }
      if (!document.defaultView) {
        return 17;
      }
      // Creating invisible container
      const outer = document.createElement('div');
      outer.style.visibility = 'hidden';
      outer.style.overflow = 'scroll'; // forcing scrollbar to appear
      document.body.appendChild(outer);
      // Creating inner element and placing it in the container
      const inner = document.createElement('div');
      outer.appendChild(inner);
      // Calculating difference between container's full width and the child width
      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      // Removing temporary elements from the DOM
      if (outer.parentNode) {
        outer.parentNode.removeChild(outer);
      }
      return scrollbarWidth;
    }
    _getComponentRootNode(componentRef) {
      return componentRef.hostView.rootNodes[0];
    }
    getComponentRef(comp, injector) {
      let compRef;
      if (isComponentWithModule(comp)) {
        const moduleRef = createNgModule(comp.module, injector);
        compRef = createComponent(comp.component, {
          environmentInjector: moduleRef.injector
        });
      } else if (isDefaultExport(comp)) {
        compRef = createComponent(comp.default, {
          environmentInjector: injector
        });
      } else {
        compRef = createComponent(comp, {
          environmentInjector: injector
        });
      }
      return compRef;
    }
    createInjector(dialogRef, tag, data) {
      return createEnvironmentInjector([{
        provide: tag.dialogData,
        useValue: data
      }, {
        provide: tag.dialogRef,
        useValue: dialogRef
      }], this.injector);
    }
    closeDialog(compRef) {
      compRef.destroy();
      this.appRef.detachView(compRef.hostView);
      this.removeOverFlowClass();
    }
    addOverFlowClass() {
      const htmlElement = this.document.querySelector('html');
      if (!htmlElement) {
        return;
      }
      htmlElement.style.overflow = 'hidden';
      htmlElement.style.paddingRight = `${this.scrollBarWidth}px`;
    }
    removeOverFlowClass() {
      const htmlElement = this.document.querySelector('html');
      if (!htmlElement) {
        return;
      }
      htmlElement.style.overflow = '';
      htmlElement.style.paddingRight = '0';
    }
    static {
      this.ɵfac = function DialogService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DialogService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DialogService,
        factory: DialogService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DialogService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DialogRef, DialogService, DialogTag };
